import React, { useState, useEffect } from 'react';
import LoadingSpinner from './LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { Bell, FileText, UserCog, User, Info, Save, ArrowLeft, X, CheckCircle, AlertCircle, Pin, Phone, MessageCircle, Zap, Hand, Sparkles } from 'lucide-react';
import TemplatesEditor from './TemplatesEditor';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface NotificationSettings {
  notify_professional_on_new_appointment: boolean;
  notify_professional_on_cancellation: boolean;
  notify_client_on_confirmation: boolean;
  notify_client_24h_reminder: boolean;
  notify_client_on_cancellation: boolean;
  whatsapp_instructions: string;
  whatsapp_meta_enabled: boolean;  // Pivot migración 011: activar WhatsApp Business automático
}

const ProfessionalNotificationConfig: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [settings, setSettings] = useState<NotificationSettings>({
    notify_professional_on_new_appointment: false,
    notify_professional_on_cancellation: false,
    notify_client_on_confirmation: true,
    notify_client_24h_reminder: true,
    notify_client_on_cancellation: true,
    whatsapp_instructions: '',
    whatsapp_meta_enabled: false  // Default OFF: la secre envía manualmente
  });

  // Obtener ID del profesional
  const getUserId = (): number | null => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        return user.id;
      } catch {
        return null;
      }
    }
    return null;
  };

  // Cargar configuración actual
  useEffect(() => {
    const loadSettings = async () => {
      const userId = getUserId();
      if (!userId) {
        navigate('/login');
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/notifications/settings/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.settings) {
            // Asegurar valores por defecto si vienen null/undefined
            setSettings({
              ...data.settings,
              whatsapp_instructions: data.settings.whatsapp_instructions || '',
              whatsapp_meta_enabled: data.settings.whatsapp_meta_enabled === true
            });
          }
        }
      } catch (err) {
        console.error('Error cargando configuración:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [navigate]);

  // Guardar configuración
  const handleSave = async () => {
    const userId = getUserId();
    if (!userId) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notifications/settings/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Configuración guardada exitosamente');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error(data.error || 'Error al guardar');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Toggle individual
  const handleToggle = (key: keyof NotificationSettings) => {
    if (key !== 'whatsapp_instructions') {
      setSettings(prev => ({
        ...prev,
        [key]: !prev[key]
      }));
    }
  };

  // Componente Toggle
  const Toggle: React.FC<{
    enabled: boolean;
    onChange: () => void;
    disabled?: boolean;
  }> = ({ enabled, onChange, disabled }) => (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className={`
        relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
        ${enabled ? 'bg-blue-600' : 'bg-gray-200'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <span
        className={`
          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
          transition duration-200 ease-in-out
          ${enabled ? 'translate-x-5' : 'translate-x-0'}
        `}
      />
    </button>
  );

  // Componente de vista previa de WhatsApp
  const WhatsAppPreview = ({ type = 'confirmation' }: { type?: 'confirmation' | 'reminder' }) => {
    const exampleInstructions = settings.whatsapp_instructions ||
      "Traer estudios previos y ropa cómoda. Llegar 10 minutos antes.";

    const previews = {
      confirmation: {
        title: "Confirmación de Turno",
        content: `Hola [Nombre del Paciente], tu cita fue agendada con éxito:\n- Profesional: Dr. Ejemplo\n- Fecha: 15/02/2026\n- Hora: 16:30 hs\n- Servicio: Consulta\n\nIndicaciones:\n${exampleInstructions}`
      },
      reminder: {
        title: "Recordatorio de Turno",
        content: `Hola [Nombre del Paciente], te recordamos tu turno:\n- Mañana 15/02/2026 a las 16:30 hs\n- Con: Dr. Ejemplo\n- Dirección: Consultorio Central\n\nIndicaciones importantes:\n${exampleInstructions}\n\nWhatsApp del profesional: +5491122334455`
      }
    };

    const preview = previews[type];

    return (
      <div className="bg-green-50 p-4 rounded-2xl rounded-tl-none max-w-xs border border-green-100">
        <div className="text-sm whitespace-pre-line">
          <p className="font-medium text-green-800">{preview.title}</p>
          <div className="mt-2 text-gray-700">
            {preview.content.split('\n').map((line, index) => (
              <p key={index} className={line.includes('Indicaciones') ? 'font-medium mt-2' : ''}>
                {line}
              </p>
            ))}
          </div>
        </div>
        <p className="text-xs text-gray-400 text-right mt-2">2:00 PM</p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size={48} label="Cargando configuración..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800 mb-3 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Volver
          </button>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="w-6 h-6 text-blue-600 flex-shrink-0" />
            <span>Configuración de Notificaciones</span>
          </h1>
          <p className="text-gray-600 mt-1">
            Elegí qué notificaciones querés recibir y personalizá los mensajes de WhatsApp
          </p>
        </div>

        {/* Mensajes de error/éxito */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
            <span className="text-red-700">{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
            <span className="text-green-700">{success}</span>
            <button onClick={() => setSuccess(null)} className="text-green-500 hover:text-green-700"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* ===================== SECCIÓN WHATSAPP (PRINCIPAL) ===================== */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
          {/* Header de la card */}
          <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">WhatsApp</h2>
              <p className="text-sm text-gray-500">Elegí cómo querés que salgan los mensajes</p>
            </div>
          </div>

          {/* Segmented control estilo iOS */}
          <div className="px-6 pt-5">
            <div className="relative bg-gray-100 rounded-xl p-1 flex">
              <button
                type="button"
                onClick={() => setSettings((s) => ({ ...s, whatsapp_meta_enabled: false }))}
                className={`relative flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                  !settings.whatsapp_meta_enabled
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Hand className="w-4 h-4" />
                Manual
              </button>
              <button
                type="button"
                onClick={() => setSettings((s) => ({ ...s, whatsapp_meta_enabled: true }))}
                className={`relative flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                  settings.whatsapp_meta_enabled
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Zap className="w-4 h-4" />
                Automático
              </button>
            </div>
            <p className="text-xs text-gray-500 text-center mt-2">
              {!settings.whatsapp_meta_enabled
                ? 'La secretaria envía cada mensaje desde su WhatsApp'
                : 'El sistema envía los mensajes solo, sin intervención'}
            </p>
          </div>

          {/* Contenido según el modo */}
          <div className="px-6 pb-6 pt-4">
            {!settings.whatsapp_meta_enabled ? (
              /* ===================== MODO MANUAL ===================== */
              <div className="space-y-3">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex gap-3">
                    <Bell className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Cómo funciona</p>
                      <p className="text-sm text-gray-700 mt-1 leading-relaxed">
                        Desde el calendario, abrí el panel <strong>Recordatorios</strong>. Vas a
                        ver los turnos de hoy y mañana con un botón para abrir WhatsApp con el
                        mensaje ya redactado. El paciente ve tu número y puede responderte
                        directamente.
                      </p>
                    </div>
                  </div>
                </div>

                <ul className="space-y-2.5 text-sm text-gray-700 pl-1">
                  <li className="flex gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>Sin costos adicionales por mensaje</span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>El paciente responde a tu número real</span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>Plantillas editables con los datos del turno</span>
                  </li>
                </ul>
              </div>
            ) : (
              /* ===================== MODO AUTOMÁTICO (META) ===================== */
              <div className="space-y-4">
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-900 text-sm">Antes de activar</p>
                      <p className="text-sm text-amber-800 mt-1 leading-relaxed">
                        Los mensajes salen desde un número comercial de Meta. Si el paciente
                        responde a ese mensaje, <strong>no te llega a vos</strong>. Puede
                        provocar cancelaciones que pasan desapercibidas.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Instrucciones para incluir en los mensajes automáticos */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-900 mb-2">
                    <FileText className="w-4 h-4 text-gray-500" />
                    Indicaciones que se agregan al final del mensaje
                  </label>
                  <textarea
                    value={settings.whatsapp_instructions}
                    onChange={(e) =>
                      setSettings({ ...settings, whatsapp_instructions: e.target.value })
                    }
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder="Ej: Traer estudios previos, ropa cómoda, llegar 10 min antes..."
                  />
                </div>

                {/* Nota: los eventos que disparan los envíos se configuran más abajo */}
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 flex gap-3">
                  <Info className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-600">
                    Qué eventos disparan los envíos automáticos (confirmación, recordatorio,
                    cancelación) se configura en la sección <strong>"Notificaciones a tus
                    pacientes"</strong> más abajo. Cuando Automático está activado, esos toggles
                    también habilitan el WhatsApp Meta además del email.
                  </p>
                </div>

                {/* Vista previa solo en modo Meta */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">Vista previa:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Confirmación:</p>
                      <WhatsAppPreview type="confirmation" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Recordatorio 24hs:</p>
                      <WhatsAppPreview type="reminder" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sección: Notificaciones al Profesional */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center mb-4">
            <UserCog className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Notificaciones para vos</h2>
              <p className="text-sm text-gray-500">Recibí alertas cuando tus pacientes agenden o cancelen turnos</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Nuevo turno */}
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex-1">
                <p className="font-medium text-gray-900">Nuevo turno agendado</p>
                <p className="text-sm text-gray-500">Recibir email y WhatsApp cuando un paciente agenda un turno</p>
              </div>
              <Toggle
                enabled={settings.notify_professional_on_new_appointment}
                onChange={() => handleToggle('notify_professional_on_new_appointment')}
              />
            </div>

            {/* Cancelación */}
            <div className="flex items-center justify-between py-3">
              <div className="flex-1">
                <p className="font-medium text-gray-900">Turno cancelado</p>
                <p className="text-sm text-gray-500">Recibir notificación cuando un paciente cancela</p>
              </div>
              <Toggle
                enabled={settings.notify_professional_on_cancellation}
                onChange={() => handleToggle('notify_professional_on_cancellation')}
              />
            </div>
          </div>
        </div>

        {/* Sección: Notificaciones al Paciente (gate de email + Meta si está activo) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center mb-4">
            <User className="w-6 h-6 text-purple-600 mr-3 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Notificaciones a tus pacientes</h2>
              <p className="text-sm text-gray-500">
                {settings.whatsapp_meta_enabled
                  ? 'Controla emails + WhatsApp automáticos'
                  : 'Controla los emails (el WhatsApp manual se envía desde el panel del calendario)'}
              </p>
            </div>
          </div>

          <div className="space-y-1">
            {/* Confirmación */}
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex-1 pr-3">
                <p className="font-medium text-gray-900">Confirmación de turno</p>
                <p className="text-sm text-gray-500">Al crear el turno</p>
              </div>
              <Toggle
                enabled={settings.notify_client_on_confirmation}
                onChange={() => handleToggle('notify_client_on_confirmation')}
              />
            </div>

            {/* Recordatorio 24hs */}
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex-1 pr-3">
                <p className="font-medium text-gray-900">Recordatorio 24 horas antes</p>
                <p className="text-sm text-gray-500">Un día antes del turno</p>
              </div>
              <Toggle
                enabled={settings.notify_client_24h_reminder}
                onChange={() => handleToggle('notify_client_24h_reminder')}
              />
            </div>

            {/* Cancelación */}
            <div className="flex items-center justify-between py-3">
              <div className="flex-1 pr-3">
                <p className="font-medium text-gray-900">Aviso de cancelación</p>
                <p className="text-sm text-gray-500">Si cancelás el turno</p>
              </div>
              <Toggle
                enabled={settings.notify_client_on_cancellation}
                onChange={() => handleToggle('notify_client_on_cancellation')}
              />
            </div>
          </div>
        </div>

        {/* Plantillas de mensajes (ambos modos las usan) */}
        {getUserId() && (
          <div className="mb-6">
            <TemplatesEditor professionalId={getUserId() as number} />
          </div>
        )}

        {/* Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <Info className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">¿Cómo funcionan las notificaciones?</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Los <strong>emails</strong> de confirmación y recordatorio se envían automáticamente siempre</li>
                <li>Los <strong>WhatsApp automáticos</strong> solo salen si activaste "Automático" arriba</li>
                <li>En modo <strong>manual</strong>, usá el botón "Recordatorios" en el calendario para enviar desde tu WhatsApp</li>
                <li>Las plantillas usan variables que se reemplazan por los datos del turno</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Botón Guardar */}
        <div className="flex justify-end space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`
              px-6 py-3 rounded-lg font-medium text-white transition-all
              ${saving 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg'
              }
            `}
          >
            {saving ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Guardando...
              </span>
            ) : (
              <span className="flex items-center gap-2"><Save className="w-4 h-4" /> Guardar configuración</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalNotificationConfig;